import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';

const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };
const PRIORITY_COLORS = {
  critical: { bg: 'rgba(239,68,68,0.15)', text: '#EF4444', label: 'קריטי' },
  high: { bg: 'rgba(245,158,11,0.15)', text: '#F59E0B', label: 'גבוה' },
  medium: { bg: 'rgba(59,130,246,0.15)', text: '#3B82F6', label: 'בינוני' },
  low: { bg: 'rgba(148,163,184,0.15)', text: '#94A3B8', label: 'נמוך' },
};
const STATUS_OPTIONS = [
  { value: 'pending', label: 'ממתין', color: '#94A3B8', bg: 'rgba(148,163,184,0.12)' },
  { value: 'in_progress', label: 'בטיפול', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  { value: 'done', label: 'בוצע', color: '#22C55E', bg: 'rgba(34,197,94,0.12)' },
  { value: 'skipped', label: 'דילוג', color: '#94A3B8', bg: 'rgba(148,163,184,0.08)' },
];
const SOURCE_LABELS = {
  scan: { label: 'סריקה', color: '#3B82F6', bg: 'rgba(59,130,246,0.12)' },
  competitor: { label: 'מתחרים', color: '#A855F7', bg: 'rgba(168,85,247,0.12)' },
  manual: { label: 'ידני', color: '#22C55E', bg: 'rgba(34,197,94,0.12)' },
  monthly_plan: { label: 'תוכנית חודשית', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  monthly_check: { label: 'בדיקות חודשיות', color: '#14B8A6', bg: 'rgba(20,184,166,0.12)' },
  geo: { label: 'GEO', color: '#06B6D4', bg: 'rgba(6,182,212,0.12)' },
};

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch(e) { return dateStr; }
}

function formatHours(h) {
  if (!h || h === 0) return '-';
  if (h < 1) return Math.round(h * 60) + ' דק\'';
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  if (mins === 0) return hours + ' שעות';
  return hours + ':' + String(mins).padStart(2, '0');
}

function getMonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
    const label = d.toLocaleDateString('he-IL', { year: 'numeric', month: 'long' });
    opts.push({ value: val, label: label });
  }
  return opts;
}

function getCurrentMonth() {
  const now = new Date();
  return now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
}

// Inline Edit Cell
function EditableCell({ value, onSave, type, options, placeholder, multiline }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);

  const ist = { background: 'var(--bg)', border: '1px solid var(--accent)', borderRadius: 4, color: 'var(--text)', fontFamily: 'inherit', outline: 'none' };

  if (!editing) {
    return (
      <span onClick={() => setEditing(true)} style={{ cursor: 'pointer', minWidth: 40, display: 'inline-block', whiteSpace: multiline ? 'pre-wrap' : 'normal' }} title="לחץ לעריכה">
        {type === 'select' ? (options.find(o => o.value === value) || {}).label || value || '-' :
         type === 'date' ? formatDate(value) :
         type === 'hours' ? formatHours(value) :
         value || <span style={{ color: 'var(--text-muted)' }}>{placeholder || '-'}</span>}
      </span>
    );
  }

  const save = () => { setEditing(false); if (draft !== value) onSave(draft); };

  if (type === 'select') {
    return (
      <select autoFocus value={draft}
        onChange={e => { setDraft(e.target.value); setEditing(false); onSave(e.target.value); }}
        onBlur={() => setEditing(false)}
        style={{ ...ist, padding: '4px 6px', fontSize: 12, width: '100%' }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (type === 'date') {
    return <input autoFocus type="date" value={draft} onChange={e => setDraft(e.target.value)} onBlur={save} onKeyDown={e => e.key === 'Enter' && save()} style={{ ...ist, padding: '4px 6px', fontSize: 12, width: 130 }} />;
  }
  if (type === 'hours') {
    return <input autoFocus type="number" step="0.25" min="0" max="24" value={draft} onChange={e => setDraft(parseFloat(e.target.value) || 0)} onBlur={save} onKeyDown={e => e.key === 'Enter' && save()} style={{ ...ist, padding: '4px 6px', fontSize: 12, width: 70, textAlign: 'center' }} />;
  }
  if (multiline) {
    return <textarea autoFocus value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={save}
      onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey || e.shiftKey)) { e.preventDefault(); save(); } }}
      rows={Math.max(3, (String(draft).match(/\n/g) || []).length + 1)}
      placeholder={placeholder}
      style={{ ...ist, padding: '4px 8px', fontSize: 12, width: '100%', resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit', boxSizing: 'border-box' }} />;
  }
  return <input autoFocus value={draft} onChange={e => setDraft(e.target.value)} onBlur={save} onKeyDown={e => e.key === 'Enter' && save()} placeholder={placeholder} style={{ ...ist, padding: '4px 8px', fontSize: 12, width: '100%' }} />;
}

// Styles
const thStyle = { padding: '10px 12px', textAlign: 'right', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 12px', verticalAlign: 'top', fontSize: 13 };
const formInputStyle = { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };
const toggleBtnStyle = { padding: '6px 12px', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' };
const toggleActiveStyle = { background: 'rgba(59,130,246,0.1)', color: 'var(--accent)', fontWeight: 600 };
const actionBtnStyle = { width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', padding: 0 };

export default function SiteChecklist() {
  const { id } = useParams();
  const [viewMode, setViewMode] = useState('all_tasks');
  const [displayMode, setDisplayMode] = useState('table');
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [items, setItems] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [newTask, setNewTask] = useState({ description: '', priority: 'medium', type: 'manual', target_url: '', notes: '', assigned_to: '', due_date: '', markDone: true });
  const [executingId, setExecutingId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());

  const monthOptions = getMonthOptions();

  useEffect(() => { loadData(); }, [id, selectedMonth]);

  async function loadData() {
    setLoading(true);
    try {
      const [scanData, summaryData, tasksData, teamData] = await Promise.all([
        api.getSiteScans(id),
        api.getChecklistSummary(id),
        api.getSiteTasks(id, null, null, null, selectedMonth),
        api.getTeamMembers().catch(() => []),
      ]);
      setScans(scanData);
      setSummary(summaryData);
      setAllTasks(tasksData.tasks || []);
      setTeamMembers(Array.isArray(teamData) ? teamData : (teamData.team || teamData.members || []));
      if (scanData.length > 0 && viewMode === 'by_scan') await loadChecklist(scanData[0].id);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function loadChecklist(scanId) {
    setSelectedScan(scanId);
    try {
      const data = await api.getChecklist(scanId);
      setItems(data.items || []);
    } catch (err) {
      try { const gen = await api.generateChecklist(scanId); setItems(gen.items || []); } catch (e) { console.error(e); }
    }
  }

  const updateTask = useCallback(async (itemId, field, value) => {
    const body = {};
    body[field] = value;
    try {
      await api.updateChecklistItem(itemId, body);
      const updater = t => {
        if (t.id !== itemId) return t;
        const u = { ...t, [field]: value };
        if (field === 'status' && value === 'done') u.completed_at = new Date().toISOString();
        if (field === 'status' && value !== 'done') u.completed_at = null;
        return u;
      };
      setAllTasks(prev => prev.map(updater));
      setItems(prev => prev.map(updater));
      const summaryData = await api.getChecklistSummary(id);
      setSummary(summaryData);
    } catch (err) { console.error(err); }
  }, [id]);

  async function handleAddTask() {
    if (!newTask.description.trim()) return;
    try {
      const taskData = {
        site_id: parseInt(id),
        type: newTask.type || 'manual',
        description: newTask.description,
        priority: newTask.priority,
        source: 'manual',
        target_url: newTask.target_url || null,
        notes: newTask.notes || null,
        assigned_to: newTask.assigned_to || null,
        due_date: newTask.due_date || null,
        // Add the task to the month being viewed (backend backdates it when
        // it isn't the current month), and set the status on create so a
        // done task is stamped inside that month too.
        month: selectedMonth,
        status: newTask.markDone ? 'done' : 'pending',
      };
      await api.createTask(taskData);
      setNewTask({ description: '', priority: 'medium', type: 'manual', target_url: '', notes: '', assigned_to: '', due_date: '', markDone: true });
      setShowAddForm(false);
      const tasksData = await api.getSiteTasks(id, null, null, null, selectedMonth);
      setAllTasks(tasksData.tasks || []);
    } catch (err) { console.error(err); }
  }

  const [addingMonthly, setAddingMonthly] = useState(false);
  async function handleAddMonthlyChecks() {
    setAddingMonthly(true);
    try {
      const res = await api.addMonthlyChecks(id, selectedMonth);
      const tasksData = await api.getSiteTasks(id, null, null, null, selectedMonth);
      setAllTasks(tasksData.tasks || []);
      const summaryData = await api.getChecklistSummary(id);
      setSummary(summaryData);
      const monthLabel = (monthOptions.find(m => m.value === selectedMonth) || {}).label || selectedMonth;
      if (res.created > 0) {
        alert('נוספו ' + res.created + ' בדיקות חודשיות ל' + monthLabel + (res.skipped ? ' (' + res.skipped + ' כבר קיימות)' : ''));
      } else {
        alert('כל הבדיקות החודשיות כבר קיימות ב' + monthLabel);
      }
    } catch (err) { alert('שגיאה: ' + (err.message || 'Unknown')); }
    finally { setAddingMonthly(false); }
  }

  async function handleDelete(itemId) {
    if (!window.confirm('למחוק את המשימה?')) return;
    try {
      await api.deleteChecklistItem(itemId);
      setAllTasks(prev => prev.filter(t => t.id !== itemId));
      setItems(prev => prev.filter(t => t.id !== itemId));
    } catch (err) { console.error(err); }
  }

  const CLAUDE_TYPES = ["meta_title","meta_description","add_faq","add_schema","alt_tags","content_expansion","h2_h3_missing","cta_rewrite"];

  async function handleExecute(task) {
    setExecutingId(task.id);
    try {
      const result = await api.executeChecklistItem(task.id, { target_keyword: task.source_keyword || "" });
      setPreviewData(result);
      setPreviewItem(task);
    } catch (err) { alert("שגיאה: " + (err.message || "Unknown")); }
    finally { setExecutingId(null); }
  }

  async function handlePublish() {
    if (!previewData || !previewItem) return;
    setPublishing(true);
    try {
      const result = await api.publishChecklistItem(previewItem.id, { generated_content: previewData.generated_content });
      if (result.success) {
        setAllTasks(prev => prev.map(t => t.id === previewItem.id ? { ...t, status: "done" } : t));
        setItems(prev => prev.map(t => t.id === previewItem.id ? { ...t, status: "done" } : t));
        const summaryData = await api.getChecklistSummary(id);
        setSummary(summaryData);
        alert("הועלה בהצלחה!");
        setPreviewData(null); setPreviewItem(null);
      } else { alert("שגיאה: " + (result.message || "העלאה נכשלה")); }
    } catch (err) { alert("שגיאה: " + (err.message || "Unknown")); }
    finally { setPublishing(false); }
  }

  function navigateMonth(direction) {
    const idx = monthOptions.findIndex(m => m.value === selectedMonth);
    const newIdx = idx + direction;
    if (newIdx >= 0 && newIdx < monthOptions.length) {
      setSelectedMonth(monthOptions[newIdx].value);
    }
  }

  const displayItems = viewMode === 'all_tasks' ? allTasks : items;
  const filtered = displayItems
    .filter(i => { if (filter === 'pending') return i.status === 'pending' || i.status === 'in_progress'; if (filter === 'done') return i.status === 'done'; return true; })
    .filter(i => sourceFilter === 'all' || i.source === sourceFilter)
    .sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'priority') cmp = (PRIORITY_ORDER[a.priority] || 9) - (PRIORITY_ORDER[b.priority] || 9);
      else if (sortBy === 'due_date') { const da = a.due_date || '9999'; const db2 = b.due_date || '9999'; cmp = da.localeCompare(db2); }
      else if (sortBy === 'status') { const so = { pending: 0, in_progress: 1, done: 2, skipped: 3 }; cmp = (so[a.status] || 9) - (so[b.status] || 9); }
      else if (sortBy === 'assigned_to') cmp = (a.assigned_to || 'zzz').localeCompare(b.assigned_to || 'zzz');
      else { const ca = a.created_at || String(a.id); const cb = b.created_at || String(b.id); cmp = ca > cb ? 1 : ca < cb ? -1 : 0; }
      return sortDir === 'desc' ? -cmp : cmp;
    });

  const pendingCount = displayItems.filter(i => i.status === 'pending' || i.status === 'in_progress').length;
  const doneCount = displayItems.filter(i => i.status === 'done').length;
  const totalHours = displayItems.reduce((sum, t) => sum + (t.time_spent || 0), 0);
  const sourceCounts = {};
  allTasks.forEach(t => { sourceCounts[t.source || 'scan'] = (sourceCounts[t.source || 'scan'] || 0) + 1; });
  const toggleSort = (col) => { if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortBy(col); setSortDir('desc'); } };
  const SortArrow = ({ col }) => { if (sortBy !== col) return <span style={{ opacity: 0.3, fontSize: 10 }}> ↕</span>; return <span style={{ fontSize: 10 }}> {sortDir === 'asc' ? '↑' : '↓'}</span>; };
  const teamOptions = teamMembers.map(m => ({ value: m.name, label: m.name }));
  const isCurrentMonth = selectedMonth === getCurrentMonth();
  const selectedMonthLabel = (monthOptions.find(m => m.value === selectedMonth) || {}).label || selectedMonth;

  if (loading) return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>טוען...</div>;

  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '4px' }}>משימות</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>תיעוד פעולות, משימות וביצוע - לדוח החודשי</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Month Navigator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 6px' }}>
            <button onClick={() => navigateMonth(1)} disabled={monthOptions.findIndex(m => m.value === selectedMonth) >= monthOptions.length - 1}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 16, padding: '2px 6px', fontFamily: 'inherit', opacity: monthOptions.findIndex(m => m.value === selectedMonth) >= monthOptions.length - 1 ? 0.3 : 1 }}>→</button>
            <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
              style={{ background: 'transparent', border: 'none', color: isCurrentMonth ? 'var(--accent)' : 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', outline: 'none', textAlign: 'center', minWidth: 120 }}>
              {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <button onClick={() => navigateMonth(-1)} disabled={monthOptions.findIndex(m => m.value === selectedMonth) <= 0}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 16, padding: '2px 6px', fontFamily: 'inherit', opacity: monthOptions.findIndex(m => m.value === selectedMonth) <= 0 ? 0.3 : 1 }}>←</button>
          </div>
          <div style={{ display: 'flex', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <button onClick={() => setDisplayMode('table')} style={{ ...toggleBtnStyle, ...(displayMode === 'table' ? toggleActiveStyle : {}) }}>☰ טבלה</button>
            <button onClick={() => setDisplayMode('cards')} style={{ ...toggleBtnStyle, ...(displayMode === 'cards' ? toggleActiveStyle : {}) }}>▦ כרטיסים</button>
          </div>
          <button onClick={handleAddMonthlyChecks} disabled={addingMonthly} title={'הוספת תבנית הבדיקות החודשיות הקבועות לחודש ' + selectedMonthLabel}
            style={{ background: 'transparent', color: '#14B8A6', border: '1px solid #14B8A6', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: addingMonthly ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: addingMonthly ? 0.6 : 1 }}>
            {addingMonthly ? 'מוסיף...' : '🔁 בדיקות חודשיות'}
          </button>
          <button onClick={() => setShowAddForm(!showAddForm)} title={'המשימה תתווסף לחודש ' + selectedMonthLabel} style={{ background: '#3B82F6', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>+ משימה חדשה</button>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--accent)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>הוספת משימה חדשה</span>
            <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 999, background: isCurrentMonth ? 'rgba(59,130,246,0.12)' : 'rgba(245,158,11,0.15)', color: isCurrentMonth ? 'var(--accent)' : '#F59E0B' }}>
              {'לחודש ' + selectedMonthLabel}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <textarea value={newTask.description} onChange={e => setNewTask({ ...newTask, description: e.target.value })} placeholder="תיאור המשימה..." rows={3}
              style={{ ...formInputStyle, flex: 2, minWidth: 250, resize: 'vertical', lineHeight: 1.5 }} />
          </div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={newTask.priority} onChange={e => setNewTask({ ...newTask, priority: e.target.value })} style={{ ...formInputStyle, width: 100 }}>
              <option value="critical">קריטי</option><option value="high">גבוה</option><option value="medium">בינוני</option><option value="low">נמוך</option>
            </select>
            <select value={newTask.assigned_to} onChange={e => setNewTask({ ...newTask, assigned_to: e.target.value })} style={{ ...formInputStyle, width: 140 }}>
              <option value="">- אחראי -</option>
              {teamMembers.map(m => <option key={m.id || m.name} value={m.name}>{m.name}</option>)}
            </select>
            <input type="date" value={newTask.due_date} onChange={e => setNewTask({ ...newTask, due_date: e.target.value })} style={{ ...formInputStyle, width: 150 }} />
            {/* Mark as done toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: newTask.markDone ? 'rgba(34,197,94,0.08)' : 'transparent', border: '1px solid ' + (newTask.markDone ? 'rgba(34,197,94,0.3)' : 'var(--border)'), borderRadius: 6, cursor: 'pointer' }}
              onClick={() => setNewTask({ ...newTask, markDone: !newTask.markDone })}>
              <div style={{
                width: 36, height: 20, borderRadius: 10, position: 'relative', transition: 'background 0.2s',
                background: newTask.markDone ? '#22C55E' : '#94A3B8',
              }}>
                <div style={{
                  width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, transition: 'right 0.2s, left 0.2s',
                  right: newTask.markDone ? 2 : 'auto', left: newTask.markDone ? 'auto' : 2,
                }} />
              </div>
              <span style={{ fontSize: 12, color: newTask.markDone ? '#22C55E' : 'var(--text-secondary)', fontWeight: 500, whiteSpace: 'nowrap' }}>סמן כהושלם</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input value={newTask.target_url} onChange={e => setNewTask({ ...newTask, target_url: e.target.value })} placeholder="URL עמוד (אופציונלי)" dir="ltr" style={{ ...formInputStyle, flex: 1, minWidth: 180, fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }} />
            <input value={newTask.notes} onChange={e => setNewTask({ ...newTask, notes: e.target.value })} placeholder="מילת מפתח / הערות" style={{ ...formInputStyle, flex: 1, minWidth: 180 }} />
            <button onClick={handleAddTask} style={{ background: '#22C55E', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>הוסף</button>
            <button onClick={() => setShowAddForm(false)} style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>ביטול</button>
          </div>
        </div>
      )}

      {/* Summary */}
      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap' }}>
        {[{ label: 'סה"כ', value: allTasks.length, color: 'var(--text)' }, { label: 'ממתין', value: pendingCount, color: '#F59E0B' }, { label: 'בוצע', value: doneCount, color: '#22C55E' }, { label: 'שעות', value: totalHours > 0 ? totalHours.toFixed(1) : '0', color: '#3B82F6' }].map(s => (
          <div key={s.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', textAlign: 'center', minWidth: 70 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <button onClick={() => setViewMode('all_tasks')} style={{ ...toggleBtnStyle, fontSize: 12, ...(viewMode === 'all_tasks' ? toggleActiveStyle : {}) }}>כל המשימות</button>
          <button onClick={() => { setViewMode('by_scan'); if (scans.length > 0) loadChecklist(scans[0].id); }} style={{ ...toggleBtnStyle, fontSize: 12, ...(viewMode === 'by_scan' ? toggleActiveStyle : {}) }}>לפי סריקה</button>
        </div>
        <div style={{ width: 1, height: 24, background: 'var(--border)' }} />
        {['all', 'pending', 'done'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', border: filter === f ? '1px solid var(--accent)' : '1px solid var(--border)', background: filter === f ? 'rgba(59,130,246,0.1)' : 'var(--surface)', color: filter === f ? 'var(--accent)' : 'var(--text-secondary)' }}>
            {f === 'all' ? 'הכל' : f === 'pending' ? 'ממתין' : 'בוצע'}
          </button>
        ))}
        <div style={{ width: 1, height: 24, background: 'var(--border)' }} />
        <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} style={{ ...formInputStyle, width: 130, padding: '4px 8px', fontSize: 12 }}>
          <option value="all">כל המקורות</option>
          {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label} ({sourceCounts[k] || 0})</option>)}
        </select>
        {viewMode === 'by_scan' && scans.length > 0 && (
          <select value={selectedScan || ''} onChange={e => loadChecklist(parseInt(e.target.value))} style={{ ...formInputStyle, width: 200, padding: '4px 8px', fontSize: 12 }}>
            {scans.map(s => <option key={s.id} value={s.id}>{s.url} ({formatDate(s.scan_date)})</option>)}
          </select>
        )}
      </div>

      {/* TABLE VIEW */}
      {displayMode === 'table' ? (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle} />
                  <th style={thStyle} onClick={() => toggleSort('created_at')}>תאריך<SortArrow col="created_at" /></th>
                  <th style={{ ...thStyle, minWidth: 250 }}>פירוט</th>
                  <th style={thStyle}>מילת מפתח</th>
                  <th style={thStyle} onClick={() => toggleSort('assigned_to')}>אחראי<SortArrow col="assigned_to" /></th>
                  <th style={thStyle} onClick={() => toggleSort('priority')}>עדיפות<SortArrow col="priority" /></th>
                  <th style={thStyle} onClick={() => toggleSort('status')}>סטטוס<SortArrow col="status" /></th>
                  <th style={thStyle} onClick={() => toggleSort('due_date')}>יעד<SortArrow col="due_date" /></th>
                  <th style={thStyle}>זמן</th>
                  <th style={thStyle} title="הצגה בדוח החודשי">📋</th>
                  <th style={thStyle}>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>{allTasks.length === 0 ? 'אין משימות בחודש זה.' : 'אין פריטים בסינון הנוכחי'}</td></tr>
                ) : filtered.map(item => {
                  const pri = PRIORITY_COLORS[item.priority] || PRIORITY_COLORS.medium;
                  const isDone = item.status === 'done' || item.status === 'skipped';
                  const isOverdue = item.due_date && item.status !== 'done' && new Date(item.due_date) < new Date();
                  return (
                    <tr key={item.id} style={{ borderBottom: '1px solid var(--border)', opacity: isDone ? 0.55 : 1, background: isOverdue ? 'rgba(239,68,68,0.04)' : 'transparent' }}
                      onMouseEnter={e => { if (!isDone) e.currentTarget.style.background = 'var(--surface-hover)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = isOverdue ? 'rgba(239,68,68,0.04)' : 'transparent'; }}>
                      <td style={tdStyle}>
                        <button onClick={() => updateTask(item.id, 'status', item.status === 'done' ? 'pending' : 'done')}
                          style={{ width: 22, height: 22, borderRadius: 5, border: '2px solid ' + (item.status === 'done' ? '#22C55E' : 'var(--border-light)'), background: item.status === 'done' ? '#22C55E' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, padding: 0 }}>
                          {item.status === 'done' ? '✓' : ''}</button>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 12, whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{formatDate(item.created_at || item.completed_at)}</td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <EditableCell value={item.description} onSave={v => updateTask(item.id, 'description', v)} type="text" multiline options={[]} placeholder="תיאור..." />
                          {item.target_url && <a href={item.target_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--accent)', textDecoration: 'none', fontFamily: "'JetBrains Mono', monospace", direction: 'ltr', display: 'inline-block', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.target_url.replace(/^https?:\/\//, '')}</a>}
                          {item.notes && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{item.notes}</span>}
                        </div>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 12 }}><EditableCell value={item.source_keyword} onSave={v => updateTask(item.id, 'source_keyword', v)} type="text" options={[]} placeholder="-" /></td>
                      <td style={tdStyle}><EditableCell value={item.assigned_to} onSave={v => updateTask(item.id, 'assigned_to', v)} type="select" options={[{ value: '', label: '-' }, ...teamOptions]} /></td>
                      <td style={tdStyle}><EditableCell value={item.priority} onSave={v => updateTask(item.id, 'priority', v)} type="select" options={Object.entries(PRIORITY_COLORS).map(([k, v]) => ({ value: k, label: v.label }))} /></td>
                      <td style={tdStyle}><EditableCell value={item.status} onSave={v => updateTask(item.id, 'status', v)} type="select" options={STATUS_OPTIONS} /></td>
                      <td style={{ ...tdStyle, color: isOverdue ? '#EF4444' : 'var(--text-secondary)', fontWeight: isOverdue ? 600 : 400 }}><EditableCell value={item.due_date} onSave={v => updateTask(item.id, 'due_date', v || 'null')} type="date" options={[]} /></td>
                      <td style={tdStyle}><EditableCell value={item.time_spent} onSave={v => updateTask(item.id, 'time_spent', parseFloat(v) || 0)} type="hours" options={[]} /></td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <button onClick={() => updateTask(item.id, 'include_in_report', !item.include_in_report)} title={item.include_in_report ? 'יוצג בדוח' : 'לא יוצג בדוח'}
                          style={{ width: 22, height: 22, borderRadius: 4, border: 'none', background: item.include_in_report !== false ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.1)', color: item.include_in_report !== false ? '#22C55E' : '#94A3B8', cursor: 'pointer', fontSize: 14, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {item.include_in_report !== false ? '✓' : ''}</button>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {CLAUDE_TYPES.includes(item.type) && item.status !== 'done' && (
                            <button onClick={() => handleExecute(item)} disabled={executingId === item.id} title="בצע עם Claude" style={actionBtnStyle}>{executingId === item.id ? '...' : '⚡'}</button>
                          )}
                          <button onClick={() => handleDelete(item.id)} title="מחק" style={{ ...actionBtnStyle, color: '#EF4444' }}>✕</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* CARDS VIEW */
        filtered.length === 0 ? (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>אין משימות בחודש זה</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map(item => {
              const pri = PRIORITY_COLORS[item.priority] || PRIORITY_COLORS.medium;
              const src = SOURCE_LABELS[item.source] || SOURCE_LABELS.scan;
              const isDone = item.status === 'done' || item.status === 'skipped';
              return (
                <div key={item.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRight: '3px solid ' + pri.text, borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 12, opacity: isDone ? 0.5 : 1 }}>
                  <button onClick={() => updateTask(item.id, 'status', item.status === 'done' ? 'pending' : 'done')}
                    style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0, marginTop: 2, border: '2px solid ' + (item.status === 'done' ? '#22C55E' : 'var(--border-light)'), background: item.status === 'done' ? '#22C55E' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, padding: 0 }}>
                    {item.status === 'done' ? '✓' : ''}</button>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5, textDecoration: isDone ? 'line-through' : 'none', whiteSpace: 'pre-wrap' }}>{item.description}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: pri.bg, color: pri.text, fontWeight: 600 }}>{pri.label}</span>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: src.bg, color: src.color }}>{src.label}</span>
                      {item.assigned_to && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>👤 {item.assigned_to}</span>}
                      {item.due_date && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>📅 {formatDate(item.due_date)}</span>}
                      {item.time_spent > 0 && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>⏱ {formatHours(item.time_spent)}</span>}
                      {item.source_keyword && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'rgba(59,130,246,0.08)', color: 'var(--accent)' }}>{item.source_keyword}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {item.include_in_report !== false && <span title="יוצג בדוח" style={{ fontSize: 14, opacity: 0.5 }}>📋</span>}
                    {CLAUDE_TYPES.includes(item.type) && item.status !== 'done' && <button onClick={() => handleExecute(item)} disabled={executingId === item.id} style={actionBtnStyle}>{executingId === item.id ? '...' : '⚡'}</button>}
                    <button onClick={() => handleDelete(item.id)} style={{ ...actionBtnStyle, color: '#EF4444' }}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Claude Preview Modal */}
      {previewData && previewItem && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setPreviewData(null); setPreviewItem(null); }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, maxWidth: 700, width: '90%', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 12 }}>תצוגה מקדימה - {previewItem.type}</h3>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 16, fontSize: 13, whiteSpace: 'pre-wrap' }}>
              {previewData.preview_text || JSON.stringify(previewData.generated_content, null, 2)}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setPreviewData(null); setPreviewItem(null); }} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13 }}>ביטול</button>
              <button onClick={handlePublish} disabled={publishing} style={{ background: '#22C55E', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600 }}>{publishing ? 'מעלה...' : 'העלה ל-WordPress'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
